import type { View } from '../store';

/*
 * THE SIDEBAR'S OWN GLYPHS — the exact set drawn for the approved nav mockup
 * (dev-preview/nav-rework-3.html), kept verbatim so the built bar matches what
 * was signed off. They are nav-only on purpose: the shared Icon set (Icon.tsx)
 * still draws these concepts its own way elsewhere (a card chip, a filter row),
 * and those must not change — so the nav carries its own drawings rather than
 * re-pointing the shared ones. Same 24×24 frame, 1.8 stroke, round caps as Icon,
 * so they sit optically level beside the rest of the app.
 *
 * Plan is a milestone spine (dots on a line) and Activity a pulse — both redrawn
 * for this nav; the rest are tuned versions of their concept.
 */
/*
 * SIGNATURE MOTION (R21): each glyph animates to its own meaning once when its
 * row becomes active (and on hover of a row you are not on). The motion is driven
 * entirely from styles.css off these hooks — no per-view wiring in App.tsx:
 *   · pathLength="100" + base dash  → the glyph DRAWS itself on (board, plan's
 *     spine, releases, documents, backlog, sprints, approvals, activity, the money
 *     frames) — one normalized length so the draw times the same on every path.
 *   · class="g-pop"   → a part that POPS in (plan's milestones, the budget coin,
 *     the banknote's centre).
 *   · class="g-tile"  → a part that SCALES in, staggered (portfolio's four boards,
 *     the two figures on Workspace).
 *   · class="g-grow"  → bars that GROW up from the baseline (Insights).
 *   · class="g-rot"   → the gear that TURNS (Settings).
 * Shared timing/easing keeps the family uniform; all of it is off under
 * prefers-reduced-motion. See the ".nav-item .ic" block in styles.css.
 */
const GLYPHS: Partial<Record<View, string>> = {
  // My work — one person (a single figure) with a check: "me, and what I have to
  // get done". A single figure so it reads apart from the two-figure Workspace
  // glyph, with the tick that marks it as the personal to-do hub.
  mywork:
    '<circle cx="9" cy="7.5" r="3.2" pathLength="100"/><path d="M3 19.5a6 6 0 0 1 12 0" pathLength="100"/><path d="M14.5 13.6l2.4 2.4 4.1-4.4" pathLength="100"/>',
  board:
    '<rect x="3" y="4" width="18" height="16" rx="2" pathLength="100"/><path d="M9 4v16M15 4v16" pathLength="100"/>',
  plan:
    '<path d="M3 12h18" pathLength="100"/><circle class="g-pop" cx="6.5" cy="12" r="1.9"/><circle class="g-pop" cx="12" cy="12" r="2.4" fill="currentColor" stroke="none"/><circle class="g-pop" cx="17.5" cy="12" r="1.9"/>',
  // Decisions — a flowchart decision node (a diamond) with a check: a point that
  // was decided. The node draws on, the check pops.
  decisions:
    '<path d="M12 3l9 9-9 9-9-9z" pathLength="100"/><path d="M8.5 12l2.2 2.2L15.5 9.5" pathLength="100"/>',
  // Risk register (RAID) — a warning triangle with a mark: what might go wrong,
  // tracked. The triangle draws on, the mark pops.
  riskregister:
    '<path d="M12 3.2 L21.5 20 H2.5 Z" pathLength="100"/><path class="g-pop" d="M12 9.5v4.5" pathLength="100"/><circle class="g-pop" cx="12" cy="17" r="0.5" fill="currentColor" stroke="none"/>',
  // Lessons — a lightbulb (an insight to carry forward).
  lessons:
    '<path d="M9.5 18h5M10.5 21h3M12 3a6 6 0 0 0-3.8 10.6c.7.6 1.1 1.3 1.2 2.4h5.2c.1-1.1.5-1.8 1.2-2.4A6 6 0 0 0 12 3z" pathLength="100"/>',
  // Lessons library — a stacked book (the workspace-wide register).
  lessonslibrary:
    '<path d="M5 4h11a2 2 0 0 1 2 2v13H7a2 2 0 0 0-2 2z" pathLength="100"/><path d="M5 19a2 2 0 0 0 2 2h11" pathLength="100"/><path d="M9 8h6M9 11h6" pathLength="100"/>',
  // Budget & payments — a wallet: this project's money (the frame the invoices
  // draw against), distinct from the banknote the cross-board queue wears.
  budgetpay:
    '<rect x="3" y="6" width="18" height="12" rx="2" pathLength="100"/><path d="M3 10h18" pathLength="100"/><circle class="g-pop" cx="16.5" cy="14" r="1.05"/>',
  sprints:
    '<rect x="3" y="5" width="18" height="16" rx="2" pathLength="100"/><path d="M3 10h18M8 3v4M16 3v4" pathLength="100"/>',
  backlog: '<path d="M4 6h16M4 12h16M4 18h10" pathLength="100"/>',
  releases:
    '<path d="M12 2 3 7v10l9 5 9-5V7z" pathLength="100"/><path d="M3 7l9 5 9-5M12 12v10" pathLength="100"/>',
  documents:
    '<path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" pathLength="100"/><path d="M14 3v5h5" pathLength="100"/>',
  approvals:
    '<path d="M4 21c2.6-2 5.4-2 8 0" pathLength="100"/><path d="M17 4l3 3-9.5 9.5L6 18l1.5-4.5z" pathLength="100"/><path d="M14.5 6.5l3 3" pathLength="100"/>',
  activity: '<path d="M3 12h4l2.2-6 3.6 12 2.2-6H21" pathLength="100"/>',
  dashboard: '<path class="g-grow" d="M4 20V10M10 20V4M16 20v-7M21 20H3"/>',
  // Every project at a glance — a 2×2 grid of boards, the roll-up above the one
  // board the other glyphs draw.
  portfolio:
    '<rect class="g-tile" x="3" y="3" width="7" height="7" rx="1.5"/><rect class="g-tile" x="14" y="3" width="7" height="7" rx="1.5"/><rect class="g-tile" x="3" y="14" width="7" height="7" rx="1.5"/><rect class="g-tile" x="14" y="14" width="7" height="7" rx="1.5"/>',
  // The "Authorised to pay" queue — a banknote: money cleared and waiting to go
  // out, distinct from the page (documents), box (releases) and grid (portfolio).
  payments:
    '<rect x="2.5" y="6" width="19" height="12" rx="2" pathLength="100"/><circle class="g-pop" cx="12" cy="12" r="2.5"/><path d="M6 12h.01M18 12h.01"/>',
  // Payment approval — a shield with a check: the authority a payment passes
  // through before it can be paid (and the separation of duties it guards),
  // distinct from the wallet (budget) and the banknote (the pay queue).
  payapproval:
    '<path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6z" pathLength="100"/><path class="g-pop" d="M9 11.5l2 2 4-4" pathLength="100"/>',
  admin:
    '<g class="g-tile"><circle cx="9" cy="8" r="3.4"/><path d="M2.5 20a6.5 6.5 0 0 1 13 0"/></g><path class="g-tile" d="M16.5 5.2a3.4 3.4 0 0 1 0 5.6M18 14.4a6.5 6.5 0 0 1 3.5 5.6"/>',
  // Settings — an eight-tooth cog (teeth around a ring + a centre hole), not a
  // sun. 8-fold symmetric and concentric at (12,12) so it spins true on activate.
  settings:
    '<path class="g-rot" d="M9.95 5.31 L10.37 2.74 L13.63 2.74 L14.05 5.31 L15.29 5.82 L17.39 4.3 L19.7 6.61 L18.18 8.71 L18.69 9.95 L21.26 10.37 L21.26 13.63 L18.69 14.05 L18.18 15.29 L19.7 17.39 L17.39 19.7 L15.29 18.18 L14.05 18.69 L13.63 21.26 L10.37 21.26 L9.95 18.69 L8.71 18.18 L6.61 19.7 L4.3 17.39 L5.82 15.29 L5.31 14.05 L2.74 13.63 L2.74 10.37 L5.31 9.95 L5.82 8.71 L4.3 6.61 L6.61 4.3 L8.71 5.82 Z"/><circle class="g-rot" cx="12" cy="12" r="3.1"/>',
};

/** A sidebar glyph. Falls back to nothing (an empty frame) for a view with no
 *  drawing, which is caught by the nav test rather than shipping invisibly. */
export default function NavIcon({ view, size = 18 }: { view: View; size?: number }) {
  const d = GLYPHS[view] ?? '';
  return (
    <svg
      className="ic"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      dangerouslySetInnerHTML={{ __html: d }}
    />
  );
}

export const NAV_GLYPH_VIEWS = Object.keys(GLYPHS) as View[];
